/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { ExpandableTable } from "../../../components/UniversalTable";
import { w2TableColumns } from "../../../helpers";
import { KeyValue } from "views/applications-id-report/components/KeyValue";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { W2Item } from "../../../types/verified/w2";
import { DocumentCard } from "views/applications-id-report/slices/IncomeReportSlice/components/DocumentCard";
import { useMemo } from "react";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import { WarningRecommendation } from "./WarningRecommendation";
import { Show } from "shared/components";
import {
  getW2InscribeReasonCodes,
  getW2InscribeStatus,
  type W2InscribeAdditionalData
} from "./W2Table/utils";
import { IncomeSourceProvider, VerifyMethodType } from "shared/types";

type W2InscribeForm = {
  additional_data?: W2InscribeAdditionalData;
  source_status?: string;
  verifyType?: VerifyMethodType;
  income_source_id?: number;
  asset?: {
    asset?: {
      filename?: string;
    };
  };
};

const mapW2FormsWithSourceData = (item: W2Item) => {
  const inscribeItem = item as W2Item & W2InscribeForm;
  const ocrolusRows =
    item.data?.document?.income_sources?.flatMap((incSource) =>
      incSource.forms.map((form) => ({
        ...form,
        verifyType: VerifyMethodType.W2,
        income_source_id: item.income_source_id,
        additional_data: inscribeItem.additional_data,
        source_status: inscribeItem.source_status
      }))
    ) ?? [];

  if (!ocrolusRows.length && inscribeItem.additional_data)
    return [
      {
        verifyType: VerifyMethodType.W2,
        income_source_id: item.income_source_id,
        additional_data: inscribeItem.additional_data,
        source_status: inscribeItem.source_status
      } as (typeof ocrolusRows)[number]
    ];

  return ocrolusRows;
};

export const W2Table = ({
  incomeItems,
  incomeSourceProviders = {},
  incomeSourceTrustScores = {}
}: {
  incomeItems?: W2Item[];
  incomeSourceProviders?: Record<number, IncomeSourceProvider | string>;
  incomeSourceTrustScores?: Record<number, number>;
}) => {
  const { rows: flatW2s, flatWarnings } = useMemo(
    () => ({
      rows: (incomeItems?.flatMap(mapW2FormsWithSourceData) ?? []).map(
        (form) => ({
          ...form,
          income_source_provider:
            form.income_source_id !== undefined
              ? incomeSourceProviders[form.income_source_id]
              : undefined
        })
      ),
      flatWarnings:
        incomeItems?.flatMap((i) => i.data?.document?.warnings ?? []) ?? []
    }),
    [incomeItems, incomeSourceProviders]
  );

  return (
    <ExpandableTable
      cellClassName="p-4 pl-0 text-sm self-center"
      headerRowClassName="text-content-300 font-medium"
      renderExpanded={(form) => {
        const isWarningEntity = "doc_info" in form;
        const formIncomeSourceId = (form as W2InscribeForm).income_source_id;
        const isInscribeW2 =
          !isWarningEntity &&
          (formIncomeSourceId !== undefined
            ? incomeSourceProviders[formIncomeSourceId] ===
              IncomeSourceProvider.Inscribe
            : Boolean((form as W2InscribeForm).additional_data));
        const formAnalysis = isWarningEntity
          ? form?.form_analysis
          : form?.doc_analysis?.form_analysis;
        const docScore = isInscribeW2
          ? incomeSourceTrustScores[formIncomeSourceId ?? -1]
          : formAnalysis?.[0]?.form_authenticity?.score;
        const reasonCodes = isInscribeW2
          ? getW2InscribeReasonCodes((form as W2InscribeForm).additional_data)
          : formAnalysis?.[0]?.form_authenticity?.reason_codes;

        const status = isInscribeW2
          ? getW2InscribeStatus(
              (form as W2InscribeForm).additional_data,
              (form as W2InscribeForm).source_status
            )
          : parseDocStatus(docScore ?? 0);
        const rejectionReason = isWarningEntity
          ? (form?.doc_info?.rejection_reason ?? reasonCodes?.[0]?.description)
          : reasonCodes?.[0]?.description;
        const hasAsset = Boolean(form?.asset?.asset);

        return (
          <div>
            <div className="px-6 py-8 bg-background-900">
              <div className="mt-6 mb-4">
                <WarningRecommendation
                  status={status}
                  rejectionReason={rejectionReason}
                  score={docScore}
                  additionalData={{ filename: form?.asset?.asset?.filename }}
                />

                <Show if={hasAsset}>
                  <div className="h-[1px] w-full bg-alpha-40 my-3" />
                  <DocumentCard
                    score={docScore ?? 0}
                    asset={form?.asset?.asset}
                    reasonCodes={reasonCodes}
                    ignorePrint
                  />
                </Show>
              </div>

              {isWarningEntity ? null : (
                <>
                  <ContentTitle
                    title="Compensation and tax information"
                    contentClassName="space-y-2"
                  >
                    {/*
                      Missing

                      Wages tips other comp
                      Website
                      Group application
                    */}

                    <KeyValue label="Year" value={form?.year || "-"} />
                    <KeyValue
                      label="Address"
                      value={form?.fields?.employer_address_line1?.value || "-"}
                    />
                    <KeyValue
                      label="Social security wages"
                      value={form?.fields?.social_security_wages?.value || "-"}
                    />

                    <KeyValue
                      label="Medicare wages and tips"
                      value={
                        form?.fields?.medicare_wages_and_tips?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Medicare tax withheld"
                      value={form?.fields?.medicare_tax_withheld?.value || "-"}
                    />

                    <KeyValue
                      label="Social security tax withheld"
                      value={
                        form?.fields?.social_security_tax_withheld?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Social security tips"
                      value={form?.fields?.social_security_tips?.value || "-"}
                    />

                    <KeyValue
                      label="Allocated tips"
                      value={form?.fields?.allocated_tips?.value || "-"}
                    />

                    <KeyValue
                      label="Federal income tax withheld"
                      value={
                        form?.fields?.federal_income_tax_withheld?.value || "-"
                      }
                    />

                    <KeyValue
                      label="State income tax primary"
                      value={
                        form?.fields?.state_income_tax_primary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Local wages tips primary"
                      value={
                        form?.fields?.local_wages_tips_primary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Locality name primary"
                      value={form?.fields?.locality_name_primary?.value || "-"}
                    />

                    <KeyValue
                      label="Local wages tips secondary"
                      value={
                        form?.fields?.local_wages_tips_secondary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Locality name secondary"
                      value={
                        form?.fields?.locality_name_secondary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="State wages tips primary"
                      value={
                        form?.fields?.state_wages_tips_primary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="State wages tips secondary"
                      value={
                        form?.fields?.state_wages_tips_secondary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="State income tax secondary"
                      value={
                        form?.fields?.state_income_tax_secondary?.value || "-"
                      }
                    />
                  </ContentTitle>

                  <ContentTitle
                    title="Employer information"
                    contentClassName="space-y-2"
                  >
                    <KeyValue
                      label="Employer ID no."
                      value={form?.fields?.employer_id_no?.value || "-"}
                    />

                    <KeyValue
                      label="Employer name"
                      value={form?.fields?.employer_name?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address state"
                      value={form?.fields?.employer_address_state?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address city"
                      value={form?.fields?.employer_address_city?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address zip"
                      value={form?.fields?.employer_address_zip?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address line1"
                      value={form?.fields?.employer_address_line1?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address line2"
                      value={form?.fields?.employer_address_line2?.value || "-"}
                    />

                    <KeyValue
                      label="Employer state no. primary"
                      value={form?.fields?.state_primary?.value || "-"}
                    />
                  </ContentTitle>

                  <ContentTitle
                    title="Employee information"
                    contentClassName="space-y-2"
                  >
                    <KeyValue
                      label="Employee name"
                      value={form?.fields?.employee_name?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address state"
                      value={form?.fields?.employee_address_state?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address city"
                      value={form?.fields?.employee_address_city?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address line1"
                      value={form?.fields?.employee_address_line1?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address line2"
                      value={form?.fields?.employee_address_line2?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address zip"
                      value={form?.fields?.employee_address_zip?.value || "-"}
                    />

                    <KeyValue
                      label="Statutory employee"
                      value={form?.fields?.statutory_employee?.value || "-"}
                    />
                  </ContentTitle>

                  <ContentTitle
                    title="Additional information"
                    contentClassName="space-y-2"
                  >
                    <KeyValue
                      label="Box9"
                      value={form?.fields?.box9?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 a code"
                      value={form?.fields?.box12a_code?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 b code"
                      value={form?.fields?.box12b_code?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 c code"
                      value={form?.fields?.box12c_code?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 d code"
                      value={form?.fields?.box12dcode?.value || "-"}
                    />

                    <KeyValue
                      label="Box14 other"
                      value={form?.fields?.box14_other?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 a amount"
                      value={form?.fields?.box12a_amount?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 b amount"
                      value={form?.fields?.box12b_amount?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 c amount"
                      value={form?.fields?.box12c_amount?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 d amount"
                      value={form?.fields?.box12d_amount?.value || "-"}
                    />

                    <KeyValue
                      label="Qualified plans"
                      value={form?.fields?.qualified_plans?.value || "-"}
                    />

                    <KeyValue
                      label="Retirement plan"
                      value={form?.fields?.retirement_plan?.value || "-"}
                    />

                    <KeyValue
                      label="State primary"
                      value={form?.fields?.state_primary?.value || "-"}
                    />

                    <KeyValue
                      label="State secondary"
                      value={form?.fields?.state_secondary?.value || "-"}
                    />

                    <KeyValue
                      label="Third-party sick pay"
                      value={form?.fields?.third_party_sick_pay?.value || "-"}
                    />
                    <KeyValue
                      label="Locality name primary"
                      value={form?.fields?.locality_name_primary?.value || "-"}
                    />

                    <KeyValue
                      label="Dependent care benefits"
                      value={
                        form?.fields?.dependent_care_benefits?.value || "-"
                      }
                    />
                  </ContentTitle>
                </>
              )}
            </div>
          </div>
        );
      }}
      rows={[...flatWarnings, ...flatW2s]}
      columns={w2TableColumns}
    />
  );
};
